import { useCallback, useEffect, useRef, useState } from 'react';

// First supported container wins. Chrome/Edge/Firefox → webm/opus, Safari → mp4.
// The backend transcodes whatever we send, so any of these is fine.
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'];

export const isVoiceRecordingSupported = () =>
  typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

const pickMimeType = () => MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));

const releaseStream = (recorder) => {
  recorder?.stream.getTracks().forEach((track) => track.stop());
};

/**
 * Records microphone audio with the native MediaRecorder.
 *
 * start()  — asks for mic permission and begins recording (rejects on denial).
 * stop()   — resolves with the recorded Blob.
 * cancel() — discards the recording, resolves with null.
 */
export const useVoiceRecorder = () => {
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!recording) return undefined;
    const interval = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [recording]);

  // Release the microphone if the screen unmounts mid-recording.
  useEffect(() => () => releaseStream(recorderRef.current), []);

  const start = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };

    recorderRef.current = recorder;
    recorder.start();
    setSeconds(0);
    setRecording(true);
  }, []);

  // MediaRecorder flushes the last chunk asynchronously, so the Blob is only
  // complete inside onstop.
  const finish = useCallback(
    (discard) =>
      new Promise((resolve) => {
        const recorder = recorderRef.current;
        if (!recorder) {
          resolve(null);
          return;
        }

        recorder.onstop = () => {
          releaseStream(recorder);
          recorderRef.current = null;
          setRecording(false);
          resolve(discard ? null : new Blob(chunksRef.current, { type: recorder.mimeType }));
        };
        recorder.stop();
      }),
    []
  );

  const stop = useCallback(() => finish(false), [finish]);
  const cancel = useCallback(() => finish(true), [finish]);

  return { recording, seconds, start, stop, cancel };
};
